import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';

/**
 * interfaces
 */
import {
    IFacilityAddressFormControls,
    IFacilityDto,
    IFacilityFormControls,
    IFacilityFormValue,
    IUpdateFacilityPayload,
} from '../../interfaces';

/**
 * validators
 */
import { AppValidators } from '@forms/validators';

/**
 * Form management service for the Facility view.
 * Responsible for building the reactive form, patching it with API data,
 * and constructing the PUT payload from current form values.
 */
@Injectable()
export class FacilityFormService {

    /**
     * The typed reactive form group for the facility form.
     */
    private readonly _facilityForm: FormGroup<IFacilityFormControls>;

    /**
     * Exposes the facility form group to components and templates.
     */
    public get facilityForm(): FormGroup<IFacilityFormControls> {
        return this._facilityForm;
    }

    /**
     * Nested address form group.
     */
    public get addressForm(): FormGroup<IFacilityAddressFormControls> {
        return this._facilityForm.controls.address;
    }

    /**
     * @param _fb Angular FormBuilder for constructing the reactive form.
     */
    constructor(private readonly _fb: FormBuilder) {
        this._facilityForm = this._buildForm();
    }

    /**
     * Patches the form with data received from the GET /facilities API response.
     * @param dto - The facility DTO returned from the API.
     */
    public patchForm(dto: IFacilityDto): void {
        this._facilityForm.patchValue({
            name:              dto.name,
            facility_type_id:  dto.facility_type?.id ?? null,
            latitude:          dto.latitude,
            longitude:         dto.longitude,
            x_coordinate:      dto.x_coordinate,
            y_coordinate:      dto.y_coordinate,
            address: {
                building_number:   dto.address?.building_number ?? null,
                street_name:       dto.address?.street_name ?? null,
                district_name:     dto.address?.district_name ?? null,
                city_name:         dto.address?.city_name ?? null,
                zip_code:          dto.address?.zip_code ?? null,
                additional_number: dto.address?.additional_number ?? null,
                unit_number:       dto.address?.unit_number ?? null,
            },
        });
        this._facilityForm.markAsPristine();
    }

    /**
     * Resets the form to its last pristine state (values at last patch).
     */
    public resetForm(value?: Partial<IFacilityFormValue>): void {
        this._facilityForm.reset(value);
    }

    /**
     * Builds the PUT payload from the current form values.
     * @returns Payload ready for the facility update API.
     */
    public buildPayload(): IUpdateFacilityPayload {
        const value = this._facilityForm.getRawValue();

        return {
            name:             value.name!,
            facility_type_id: value.facility_type_id!,
            latitude:         value.latitude!,
            longitude:        value.longitude!,
            x_coordinate:     value.x_coordinate ?? '',
            y_coordinate:     value.y_coordinate ?? '',
            address: {
                building_number:   value.address.building_number!,
                street_name:       value.address.street_name!,
                district_name:     value.address.district_name!,
                city_name:         value.address.city_name!,
                zip_code:          value.address.zip_code!,
                additional_number: value.address.additional_number ?? 0,
                unit_number:       value.address.unit_number ?? 0,
            },
        };
    }

    /**
     * Builds and returns the initial reactive form group.
     * @returns A fully typed FormGroup for the facility form.
     */
    private _buildForm(): FormGroup<IFacilityFormControls> {
        return this._fb.group<IFacilityFormControls>({
            name:              this._fb.control<string | null>(null, AppValidators.required),
            facility_type_id:  this._fb.control<string | null>(null, AppValidators.required),
            latitude:          this._fb.control<string | null>(null, AppValidators.required),
            longitude:         this._fb.control<string | null>(null, AppValidators.required),
            x_coordinate:      this._fb.control<string | null>(null),
            y_coordinate:      this._fb.control<string | null>(null),
            address: this._fb.group<IFacilityAddressFormControls>({
                building_number:   this._fb.control<number | null>(null, AppValidators.required),
                street_name:       this._fb.control<string | null>(null, AppValidators.required),
                district_name:     this._fb.control<string | null>(null, AppValidators.required),
                city_name:         this._fb.control<string | null>(null, AppValidators.required),
                zip_code:          this._fb.control<number | null>(null, AppValidators.required),
                additional_number: this._fb.control<number | null>(null),
                unit_number:       this._fb.control<number | null>(null),
            }),
        });
    }

}
